import {MediaCardType} from '@wandb/weave/common/types/media';

import {MediaConfigWithDefaults} from '../../../PanelMediaBrowser/Component';
import {MAX_COL_COUNT, MIN_COL_COUNT} from '../../../PanelMediaBrowser/config';
import {ChartTitle} from './components/ChartTitle';
import {FitImage} from './components/FitImage';
import {GridMode} from './components/GridMode';
import {MaxItemCount} from './components/MaxItemCount';
import {MaxRuns} from './components/MaxRuns';
import {MediaKeys} from './components/MediaKeys';
import {NumberOfColumns} from './components/NumberOfColumns';
import {OriginalSize} from './components/OriginalSize';
import {SampleComponent} from './components/SampleComponent';
import {SmoothImage} from './components/SmoothImage';
import {MediaSettingsSection, MediaSettingsTab} from './types';

export const MEDIA_SETTINGS_TABS: MediaSettingsTab[] = [
  {
    title: 'Overlays',
    sections: [
      {
        title: 'Bounding boxes',
        settings: [
          {
            component: SampleComponent,
            mediaTypes: ['image'],
          },
        ],
      },
      {
        title: 'Masks',
        settings: [
          {
            component: SampleComponent,
            mediaTypes: ['image'],
          },
        ],
      },
    ],
  },
  {
    title: 'Display',
    sections: [
      {
        title: 'Viewer settings',
        settings: [
          {
            component: ChartTitle,
            mediaTypes: ['image'],
          },
          {
            component: MediaKeys,
            mediaTypes: ['image'],
          },
        ],
      },
      {
        title: 'Appearance',
        settings: [
          {
            component: SmoothImage,
            mediaTypes: ['image'],
          },
        ],
      },
      {
        title: 'Navigation and playback',
        settings: [
          {
            component: SampleComponent,
            mediaTypes: ['image'],
          },
        ],
      },
    ],
  },
  {
    title: 'Layout',
    sections: [
      {
        title: 'Grid and organization',
        settings: [
          {
            component: GridMode,
            mediaTypes: ['image'],
            shouldShow: config => !config.actualSize && !config.fitToDimension, // Grid mode only makes sense if the images are already automatically sized (not original or fit to one dimension)
          },
          {
            component: NumberOfColumns,
            mediaTypes: ['image'],
            shouldShow: config =>
              MAX_COL_COUNT > MIN_COL_COUNT && // There's no point in showing the slider if there's not more than one column number option
              !config.actualSize && // actualSize is defaulted to false in `getConfigWithDefaults` so we only care if the user has manually toggled it to true
              !config.fitToDimension, // same for fitToDimension
          },
          {
            component: MaxRuns,
            mediaTypes: ['image'],
          },
          {
            component: MaxItemCount,
            mediaTypes: ['image'],
            shouldShow: config =>
              MAX_COL_COUNT > MIN_COL_COUNT && // There's no point in showing the slider if there's not more than one column number option
              !config.actualSize && // actualSize is defaulted to false in `getConfigWithDefaults` so we only care if the user has manually toggled it to true
              !config.fitToDimension && // same for fitToDimension
              config.mode === 'gallery',
          },
        ],
      },
      {
        title: 'Fit and alignment',
        settings: [
          {
            component: OriginalSize,
            mediaTypes: ['image'],
            shouldShow: config => config.mode !== 'grid',
            getComponentKey: config =>
              `${config.actualSize.toString()},${config.fitToDimension.toString()}`, // These settings are interdependent so we need to re-mount the component whenever either of them changes
          },
          {
            component: FitImage,
            mediaTypes: ['image'],
            shouldShow: config => config.mode !== 'grid',
            getComponentKey: config =>
              `${config.actualSize.toString()},${config.fitToDimension.toString()}`, // Ditto
          },
        ],
      },
    ],
  },
];

export function getMediaSettingsTabs(
  mediaType: MediaCardType,
  config: MediaConfigWithDefaults
): MediaSettingsTab[] {
  const initialTabs: MediaSettingsTab[] = [];
  return MEDIA_SETTINGS_TABS.reduce((tabs, tab) => {
    const initialSections: MediaSettingsSection[] = [];

    // Filter sections to only include those with relevant settings
    const filteredSections = tab.sections.reduce((sections, section) => {
      // Filter settings to only include those with relevant media types
      const filteredSettings = section.settings.filter(
        setting =>
          setting.mediaTypes.includes(mediaType) &&
          (setting.shouldShow?.(config) ?? true)
      );
      if (filteredSettings.length > 0) {
        sections.push({...section, settings: filteredSettings});
      }
      return sections;
    }, initialSections);

    // Only add tab if it has relevant sections
    if (filteredSections.length > 0) {
      tabs.push({...tab, sections: filteredSections});
    }
    return tabs;
  }, initialTabs);
}
